import { useState } from 'react';

import { Group, Paper, Stack, Text } from '@mantine/core';

import { IconChevronDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Tooltip } from '../../../shared/components/UI/Tooltip/Tooltip';

import { CPVRow } from '../../../shared/components/CPVRow';
import type Lot from '../../../shared/entities/Lot';
import type { SimplifiedTenderPreviewData } from '../../../shared/entities/Stream';
import { sortLotsByReference } from '../utils/sortLotsByReference';

const TOOLTIP_WIDTH = 350;

export function PreviewTenderCard({
  title,
  description,
  lots,
  cpvs,
  id,
}: SimplifiedTenderPreviewData) {
  const [showLotsPreview, setShowLotsPreview] = useState(false);

  return (
    <Paper
      p="04"
      radius="md"
      sx={theme => ({
        border: `1px solid ${theme.colors.gray[2]}`,
        boxShadow: theme.shadows.xs,
      })}
    >
      <Stack spacing="02">
        <Tooltip content={title} width={TOOLTIP_WIDTH}>
          <Text
            component={Link}
            sx={{ '&:hover': { textDecoration: 'underline' } }}
            to={`/notices/${id}`}
            variant="xs"
            fw={500}
            color="gray.9"
            lineClamp={2}
          >
            {title}
          </Text>
        </Tooltip>
        <Tooltip content={description} width={TOOLTIP_WIDTH}>
          <Text variant="xs" fw={400} color="gray.6" lineClamp={2}>
            {description}
          </Text>
        </Tooltip>

        <CPVRow cpvs={cpvs} />

        {lots.length ? (
          <LotsPreviewButton
            count={lots.length}
            setShowLotsPreview={setShowLotsPreview}
            show={showLotsPreview}
          />
        ) : (
          <NoLotsPreview />
        )}

        <LotsPreview lots={lots} show={showLotsPreview} />
      </Stack>
    </Paper>
  );
}

function LotsPreviewButton({
  count,
  setShowLotsPreview,
  show,
}: {
  count: number;
  setShowLotsPreview: React.Dispatch<React.SetStateAction<boolean>>;
  show: boolean;
}) {
  return (
    <Group py="01">
      <Text variant="xs" fw="500" c="gray.6">
        Lots
      </Text>
      <Group
        spacing="02"
        p="0 02"
        onClick={() => {
          setShowLotsPreview(show => !show);
        }}
        sx={theme => ({
          background: theme.colors.primary[1],
          color: theme.colors.primary[7],
          borderRadius: theme.radius.xl,
          padding: `0 ${theme.spacing['02']}`,
          border: 'none',
          cursor: 'pointer',
        })}
      >
        <Text variant="xs" fw="500">
          {count}
        </Text>

        <Group
          sx={theme => ({
            color: theme.colors.primary[7],
            transform: show ? 'rotate(-180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          })}
        >
          <IconChevronDown size={16} />
        </Group>
      </Group>
    </Group>
  );
}

function NoLotsPreview() {
  const { t } = useTranslation(['stream']);
  return (
    <Group py="01">
      <Text variant="xs" fw="500" c="gray.6">
        {t('editStreamForm.lots')}
      </Text>

      <Text variant="xs" fw="400" c="gray.9" sx={{ whiteSpace: 'nowrap' }}>
        {t('editStreamForm.nonAllotted')}
      </Text>
    </Group>
  );
}

function LotsPreview({ lots, show }: { lots: Lot[]; show: boolean }) {
  if (!lots || !show) {
    return null;
  }

  return (
    <Stack spacing="02">
      {lots.sort(sortLotsByReference).map(lot => (
        <LotCard key={lot.title} lot={lot} />
      ))}
    </Stack>
  );
}

function LotCard({ lot }: { lot: Lot }) {
  const { t } = useTranslation(['stream']);

  return (
    <Stack
      p="04"
      spacing="01"
      sx={theme => ({
        borderRadius: theme.spacing['02'],
        border: `1px solid ${theme.colors.gray[1]}`,
      })}
    >
      <Stack spacing="01">
        {lot.reference && (
          <Text variant="xs" fw={500} c="gray.5">
            {t('editStreamForm.lot')} {lot.reference}
          </Text>
        )}

        <Text variant="xs" c="gray.9">
          {lot.title}
        </Text>
      </Stack>

      <Stack spacing="01">
        {lot.cpvs && lot.cpvs.length > 0 && (
          <>
            <Text variant="xs" fw={400} c="gray.5">
              {t('editStreamForm.cpv')}
            </Text>
            <Text variant="xs" fw={400} c="gray.5">
              {lot.cpvs.map(cpv => cpv.title).join(', ')}
            </Text>
          </>
        )}
      </Stack>
    </Stack>
  );
}
